import type {
    FocusEventHandler,
    KeyboardEventHandler,
    ReactNode,
    Ref,
} from "react";
import classNames from "classnames";
import { CellLabel } from "./PickerCellLabel";
import { resolveCellChevronMode, usePickerQuery } from "./PickerContext";
import {
    PickerInputTriggerBody,
    useInputTriggerRoot,
} from "./PickerInputTriggerParts";
import type { PickerForcedState } from "./Picker.types";
import styles from "./PickerCellTrigger.module.scss";

/** Props of `Picker.CellInputTrigger`; `ref` reaches the `<input>`. */
export interface PickerCellInputTriggerProps {
    placeholder?: string;
    /** Renders the selection as plain text: no input, no chevron. */
    readOnly?: boolean;
    /** The "changed, not yet saved" wash of editable table cells. */
    pending?: boolean;
    /** `true` keeps the chevron visible at rest, `false` drops it; unset follows the root's `chevron`. */
    showChevron?: boolean;
    clearable?: boolean;
    /** Leading summary for multi-select roots; defaults to the root's selection label. */
    summary?: ReactNode;
    ref?: Ref<HTMLInputElement>;
    className?: string;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    /** Playground-only: paints a state statically. */
    "data-forced-state"?: PickerForcedState;
}

/**
 * Chromeless input trigger for ViewTable and spreadsheet cells: the cell
 * trigger's rest / hover / chevron behaviour and `--picker-trigger-padding-x`
 * around Base UI's `Combobox.Input`, without a focus ring of its own (the
 * table draws the focused cell). `readOnly` renders the selection as plain
 * text.
 */
export const PickerCellInputTrigger = ({
    placeholder = "Select",
    readOnly = false,
    pending = false,
    showChevron,
    clearable = false,
    summary,
    ref,
    className,
    onKeyDown,
    onBlur,
    "data-forced-state": forcedState,
}: PickerCellInputTriggerProps) => {
    const root = useInputTriggerRoot("Picker.CellInputTrigger");
    const { inputValue } = usePickerQuery();
    const sharedClassName = classNames(
        styles.cellTrigger,
        { [styles.cellTriggerPending]: pending },
        root.classNames.trigger,
        className,
    );
    const chevronMode = resolveCellChevronMode({ showChevron, root });
    const isChevronRendered = chevronMode !== "none";
    const isChevronForced = chevronMode === "always";

    if (readOnly) {
        // Under `allowCustomValue` the value is the input text.
        const value = root.isTextValue
            ? inputValue || null
            : root.selectionLabel;
        return (
            <span
                className={classNames(
                    sharedClassName,
                    styles.cellTriggerReadOnly,
                )}
            >
                <CellLabel
                    label={value ?? placeholder}
                    isPlaceholder={value === null}
                />
            </span>
        );
    }

    return (
        <div
            ref={root.setTriggerAnchor}
            className={classNames(sharedClassName, styles.cellInputTrigger)}
            data-picker-part="cell-input-trigger"
            data-picker-trigger={root.chainId}
            data-show-chevron={isChevronForced ? "" : undefined}
            data-forced-state={forcedState ?? root.forcedState}
        >
            <PickerInputTriggerBody
                placeholder={placeholder}
                showChevron={isChevronRendered}
                clearable={clearable}
                summary={summary}
                controlClassName={styles.cellInput}
                chevronClassName={styles.cellChevron}
                ref={ref}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />
        </div>
    );
};
